import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Numeral } from "react-numeral";
import { Menu } from "@headlessui/react";
import { CaretDownIcon } from "@radix-ui/react-icons";
import { personaScore } from "../../../apis/workspace/general";
import TooltipWrapper from "../../TooltipWrapper";
import Image from "../../Tags/Image";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const MenuButton = Menu.Button;
const MenuItems = Menu.Items;
const MenuItem = Menu.Item;

export default function IcpPersona({ contactId, personId, workspaceId }) {
  const captureEvent = useCaptureEvent();
  const [icpData, setIcpData] = useState([]);
  const [activeIcpData, setActiveIcpData] = useState({});

  const icpScoreFn = useMutation(personaScore, {
    onSuccess(data, variables, context) {
      setIcpData(data.scores ? data.scores : []);
      if (data.scores && data.scores.length > 0) {
        setActiveIcpData(data.scores[0]);
      }
    },

    onError(error) {
      console.log(error);
    },
  });

  const handleIcpScore = () => {
    if (icpScoreFn.isLoading) {
      return;
    }
    icpScoreFn.mutate({
      data: {
        contact_id: contactId,
        person_id: personId,
      },
      // contact_id: "01k861c4w4z919esdhxw5khjpw",
      options: {
        headers: {
          "X-Workspace-Id": workspaceId,
          // "X-Workspace-Id": "01jxzaksc7wewpb9awzpw9g3y2",
          // Authorization: `Bearer 57001|pr_token_LPua2FTm4Cn0W7MO2Kj9m9NpKMdZgVvv56GYsB4Re1d78ae6`,
        },
      },
    });
  };

  useEffect(() => {
    if (!workspaceId) return;

    const timeout = setTimeout(() => {
      if (!icpScoreFn.isLoading) {
        handleIcpScore();
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [workspaceId]);

  console.log("icpData", icpData);

  const getScoreLabel = (scoringTierConfig, finalScore) => {
    for (const [key, range] of Object.entries(scoringTierConfig)) {
      const [a, b] = range;
      const min = Math.min(a, b);
      const max = Math.max(a, b);

      if (finalScore >= min && finalScore <= max) {
        return key;
      }
    }
    return null;
  };

  const returnScoreColor = (label = "") => {
    if (label === "Excellent") {
      return "bg-[#079455]";
    }
    if (label === "Good") {
      return "bg-[#0086C9]";
    }
    if (label === "Fair") {
      return "bg-[#DC6803]";
    }
    if (label === "Not a fit") {
      return "bg-[#717680]";
    }
  };

  const getArrowIcon = (contribution) => {
    const value = parseInt(contribution);

    if (value >= 8) return "arrow-up-3.svg";
    if (value >= 4) return "arrow-up-2.svg";
    if (value >= 1) return "arrow-up-1.svg";

    if (value <= -7) return "arrow-down-3.svg";
    if (value <= -4) return "arrow-down-2.svg";
    if (value <= -1) return "arrow-down-1.svg";

    return null; // no change
  };

  const formatMatchingValue = (value) => {
    if (typeof value === "string") {
      return value.length > 25 ? `${value.slice(0, 25)}...` : value;
    }

    if (Array.isArray(value)) {
      const first = value[0] || "";
      const remainingCount = value.length - 1;
      return remainingCount > 0 ? (
        <span>
          {first}{" "}
          <span className="py-0.5 px-2 rounded-full border border-border bg-[#FAFAFA] dark:bg-gray-100 ml-1">
            +{remainingCount}
          </span>
        </span>
      ) : (
        first
      );
    }

    return ""; // fallback
  };

  useEffect(() => {
    console.log("icpData", icpData);
  }, [icpData]);

  const buttonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(null);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (!buttonRef.current) return;
      const { width } = buttonRef.current.getBoundingClientRect();
      setButtonWidth((prev) => {
        const roundedWidth = Math.round(width);
        if (prev !== null && Math.round(prev) === roundedWidth) {
          return prev;
        }
        return roundedWidth;
      });
    };

    updateWidth();

    if (typeof window === "undefined" || !buttonRef.current) {
      return;
    }

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(buttonRef.current);
    }

    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [icpData?.length, activeIcpData?.personaName]);

  const ReturnTierConfig = ({ activeIcpData }) => {
    return (
      <div className="whitespace-pre">
        {activeIcpData?.notEnoughData ? (
          <span className="inline-flex items-center space-x-2">
            <svg
              width="37"
              height="28"
              viewBox="0 0 37 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-gray-800"
            >
              <path
                d="M6.07911 19.304V18.9716C6.08763 17.429 6.23252 16.2017 6.51377 15.2898C6.80354 14.3778 7.21263 13.6449 7.74104 13.0909C8.26945 12.5284 8.91291 12.0128 9.67144 11.544C10.1913 11.2202 10.6558 10.8665 11.0649 10.483C11.4825 10.0909 11.8106 9.65625 12.0493 9.17898C12.2879 8.69318 12.4072 8.15199 12.4072 7.5554C12.4072 6.8821 12.2496 6.2983 11.9342 5.80398C11.6189 5.30966 11.1927 4.92614 10.6558 4.65341C10.1274 4.38068 9.53507 4.24432 8.87882 4.24432C8.27371 4.24432 7.69842 4.37642 7.15297 4.64062C6.61604 4.89631 6.16859 5.28835 5.81064 5.81676C5.46121 6.33665 5.26519 6.99716 5.22257 7.7983H0.671435C0.714049 6.17898 1.10609 4.82386 1.84757 3.73295C2.59757 2.64205 3.58621 1.82386 4.81348 1.27841C6.04928 0.732953 7.41291 0.460226 8.90439 0.460226C10.5322 0.460226 11.964 0.745738 13.1998 1.31676C14.4442 1.88778 15.4115 2.7017 16.1018 3.75852C16.8007 4.80682 17.1501 6.05114 17.1501 7.49148C17.1501 8.46307 16.9925 9.33239 16.6771 10.0994C16.3703 10.8665 15.9314 11.5483 15.3604 12.1449C14.7893 12.7415 14.1118 13.2741 13.3277 13.7429C12.6373 14.169 12.0706 14.6122 11.6274 15.0724C11.1927 15.5327 10.8689 16.0739 10.6558 16.696C10.4513 17.3097 10.3447 18.0682 10.3362 18.9716V19.304H6.07911ZM8.30354 27.2812C7.53649 27.2812 6.87598 27.0085 6.322 26.4631C5.76803 25.9176 5.49104 25.2528 5.49104 24.4688C5.49104 23.7017 5.76803 23.0455 6.322 22.5C6.87598 21.9545 7.53649 21.6818 8.30354 21.6818C9.06206 21.6818 9.71831 21.9545 10.2723 22.5C10.8348 23.0455 11.116 23.7017 11.116 24.4688C11.116 24.9886 10.9839 25.4616 10.7197 25.8878C10.464 26.3139 10.1231 26.6548 9.697 26.9105C9.27939 27.1577 8.8149 27.2812 8.30354 27.2812ZM24.9411 19.304V18.9716C24.9497 17.429 25.0945 16.2017 25.3758 15.2898C25.6656 14.3778 26.0747 13.6449 26.6031 13.0909C27.1315 12.5284 27.7749 12.0128 28.5335 11.544C29.0534 11.2202 29.5178 10.8665 29.9269 10.483C30.3445 10.0909 30.6727 9.65625 30.9113 9.17898C31.1499 8.69318 31.2693 8.15199 31.2693 7.5554C31.2693 6.8821 31.1116 6.2983 30.7963 5.80398C30.4809 5.30966 30.0548 4.92614 29.5178 4.65341C28.9894 4.38068 28.3971 4.24432 27.7409 4.24432C27.1357 4.24432 26.5605 4.37642 26.015 4.64062C25.4781 4.89631 25.0306 5.28835 24.6727 5.81676C24.3232 6.33665 24.1272 6.99716 24.0846 7.7983H19.5335C19.5761 6.17898 19.9681 4.82386 20.7096 3.73295C21.4596 2.64205 22.4482 1.82386 23.6755 1.27841C24.9113 0.732953 26.2749 0.460226 27.7664 0.460226C29.3943 0.460226 30.8261 0.745738 32.0619 1.31676C33.3062 1.88778 34.2735 2.7017 34.9639 3.75852C35.6627 4.80682 36.0122 6.05114 36.0122 7.49148C36.0122 8.46307 35.8545 9.33239 35.5391 10.0994C35.2323 10.8665 34.7934 11.5483 34.2224 12.1449C33.6514 12.7415 32.9738 13.2741 32.1897 13.7429C31.4994 14.169 30.9326 14.6122 30.4894 15.0724C30.0548 15.5327 29.7309 16.0739 29.5178 16.696C29.3133 17.3097 29.2068 18.0682 29.1982 18.9716V19.304H24.9411ZM27.1656 27.2812C26.3985 27.2812 25.738 27.0085 25.184 26.4631C24.6301 25.9176 24.3531 25.2528 24.3531 24.4688C24.3531 23.7017 24.6301 23.0455 25.184 22.5C25.738 21.9545 26.3985 21.6818 27.1656 21.6818C27.9241 21.6818 28.5803 21.9545 29.1343 22.5C29.6968 23.0455 29.9781 23.7017 29.9781 24.4688C29.9781 24.9886 29.846 25.4616 29.5818 25.8878C29.3261 26.3139 28.9852 26.6548 28.559 26.9105C28.1414 27.1577 27.6769 27.2812 27.1656 27.2812Z"
                fill="currentColor"
              />
            </svg>

            <span
              className={`bg-gray-100 text-gray-800 rounded-2xl inline-flex items-center py-1 px-2.5 text-base space-x-1`}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className=""
              >
                <g clip-path="url(#clip0_5949_27200)">
                  <path
                    d="M18.3334 9.99984H15.0001M5.00008 9.99984H1.66675M10.0001 4.99984V1.6665M10.0001 18.3332V14.9998M16.6667 9.99984C16.6667 13.6817 13.682 16.6665 10.0001 16.6665C6.31818 16.6665 3.33341 13.6817 3.33341 9.99984C3.33341 6.31794 6.31818 3.33317 10.0001 3.33317C13.682 3.33317 16.6667 6.31794 16.6667 9.99984ZM12.5001 9.99984C12.5001 11.3805 11.3808 12.4998 10.0001 12.4998C8.61937 12.4998 7.50008 11.3805 7.50008 9.99984C7.50008 8.61913 8.61937 7.49984 10.0001 7.49984C11.3808 7.49984 12.5001 8.61913 12.5001 9.99984Z"
                    stroke="currentColor"
                    stroke-width="1.875"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_5949_27200">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <span>Not enough data</span>
            </span>
          </span>
        ) : (
          <span
            className={`${returnScoreColor(
              getScoreLabel(activeIcpData?.scoringTierConfig, activeIcpData?.finalScore)
            )} text-white rounded-2xl inline-flex items-center py-1 px-2.5 text-base space-x-1`}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_5949_27200)">
                <path
                  d="M18.3334 9.99984H15.0001M5.00008 9.99984H1.66675M10.0001 4.99984V1.6665M10.0001 18.3332V14.9998M16.6667 9.99984C16.6667 13.6817 13.682 16.6665 10.0001 16.6665C6.31818 16.6665 3.33341 13.6817 3.33341 9.99984C3.33341 6.31794 6.31818 3.33317 10.0001 3.33317C13.682 3.33317 16.6667 6.31794 16.6667 9.99984ZM12.5001 9.99984C12.5001 11.3805 11.3808 12.4998 10.0001 12.4998C8.61937 12.4998 7.50008 11.3805 7.50008 9.99984C7.50008 8.61913 8.61937 7.49984 10.0001 7.49984C11.3808 7.49984 12.5001 8.61913 12.5001 9.99984Z"
                  stroke="#ECFDF3"
                  stroke-width="1.875"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_5949_27200">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <span>{getScoreLabel(activeIcpData?.scoringTierConfig, activeIcpData?.finalScore)}</span>
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      {icpData && icpData.length > 0 && (
        <div className="border rounded-[14px] border-border  bg-background relative mt-4  py-2 px-4">
          <div className="flex pt-4 pb-3 justify-between items-center ">
            <span className="text-[16px] text-text-700 mr-3 font-bold flex items-center space-x-2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18.3334 9.99984H15.0001M5.00008 9.99984H1.66675M10.0001 4.99984V1.6665M10.0001 18.3332V14.9998M16.6667 9.99984C16.6667 13.6817 13.682 16.6665 10.0001 16.6665C6.31818 16.6665 3.33341 13.6817 3.33341 9.99984C3.33341 6.31794 6.31818 3.33317 10.0001 3.33317C13.682 3.33317 16.6667 6.31794 16.6667 9.99984ZM12.5001 9.99984C12.5001 11.3805 11.3808 12.4998 10.0001 12.4998C8.61937 12.4998 7.50008 11.3805 7.50008 9.99984C7.50008 8.61913 8.61937 7.49984 10.0001 7.49984C11.3808 7.49984 12.5001 8.61913 12.5001 9.99984Z"
                  stroke="#717680"
                  stroke-width="1.875"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>Persona fitment</span>
            </span>
          </div>
          <div className="">
            <div className="w-full space-y-1">
              <div className="relative mb-4">
                <Menu as="div" className="relative block text-left w-full">
                  <MenuButton className="w-full" ref={buttonRef}>
                    <span className="bg-brand-100 dark:bg-background text-[#6941C6] dark:text-brand-700 border border-[#D6BBFB] p-2 rounded-lg flex justify-between items-center text-sm">
                      <span>{activeIcpData?.personaName}</span>
                      <CaretDownIcon />
                    </span>
                  </MenuButton>
                  <MenuItems
                    anchor="bottom start"
                    style={buttonWidth ? { minWidth: `${buttonWidth}px` } : undefined}
                    className="absolute left-0 z-20 mt-1 rounded-lg border bg-background py-2 shadow-lg"
                  >
                    {icpData &&
                      icpData.length > 0 &&
                      icpData.map((item) => (
                        <MenuItem key={item?.personaId}>
                          {({ active, close }) => {
                            const isSelected = item?.personaId === activeIcpData?.personaId;
                            const layoutClasses = item?.notEnoughData
                              ? "flex flex-col items-start space-y-2"
                              : "flex items-center justify-between";
                            const stateClasses = isSelected
                              ? "bg-[#F4EBFF] dark:bg-gray-100"
                              : active
                                ? "bg-[#F9F5FF] dark:bg-gray-100"
                                : "hover:bg-[#F9F5FF]";

                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  captureEvent("EXT_Persona_SelectClick", {
                                    persona_id: item?.personaId,
                                    persona_name: item?.personaName,
                                  });
                                  setActiveIcpData(item);
                                  close();
                                }}
                                className={`w-full rounded-md px-4 py-2 text-left transition-colors ${layoutClasses} ${stateClasses}`}
                              >
                                <span className="text-sm">{item?.personaName}</span>
                                <div className="inline-flex items-center">
                                  <ReturnTierConfig activeIcpData={item} />
                                </div>
                              </button>
                            );
                          }}
                        </MenuItem>
                      ))}
                  </MenuItems>
                </Menu>
              </div>

              {/* {icpData &&
                icpData.length > 0 &&
                icpData.map((item) => (
                  <span
                    className={`cursor-pointer block text-[12px] px-3 py-2 rounded-[8px] text-sm font-semibold ${
                      item.personaId === activeIcpData.personaId
                        ? "bg-[#F4EBFF] text-[#6941C6] border border-[#D6BBFB]"
                        : "bg-transparent text-[#414651] border border-transparent"
                    }`}
                    onClick={() => {
                      setActiveIcpData(item);
                    }}
                  >
                    {item?.personaName}
                  </span>
                ))} */}
            </div>
            <div className="w-full">
              <div className="flex items-center space-x-2">
                {activeIcpData?.finalScore && (
                  <span className="text-foreground text-3xl font-bold">{activeIcpData?.finalScore}</span>
                )}

                <ReturnTierConfig activeIcpData={activeIcpData} />
              </div>
              <div className="space-y-3 mt-3">
                {activeIcpData &&
                  activeIcpData.attributes &&
                  activeIcpData.attributes.length > 0 &&
                  activeIcpData.attributes.map((attr) => (
                    <div key={attr.display_name}>
                      {getArrowIcon(attr?.contribution) && (
                        <div className="flex justify-between items-center text-sm">
                          <span>
                            <span className="block text-[12px] text-gray-600 mb-0.5">
                              {attr?.display_name}
                            </span>
                            <span className="block">
                              {!isNaN(Number(attr?.matching_value)) ? (
                                <p>
                                  <Numeral
                                    className="uppercase"
                                    value={attr?.matching_value}
                                    format={"0.[0]a"}
                                  />
                                </p>
                              ) : (
                                <div className="capitalize">
                                  <TooltipWrapper
                                    className="text-left"
                                    content={
                                      isNaN(Number(attr?.matching_value))
                                        ? Array.isArray(attr?.matching_value)
                                          ? attr?.matching_value.join(", ")
                                          : typeof attr?.matching_value === "string" &&
                                              attr?.matching_value?.length > 25
                                            ? attr?.matching_value
                                            : ""
                                        : ""
                                    }
                                    contentClassName="max-w-[250px] leading-5 mr-10 capitalize"
                                  >
                                    {formatMatchingValue(attr?.matching_value)}
                                  </TooltipWrapper>
                                </div>
                              )}
                            </span>
                          </span>
                          <TooltipWrapper
                            className="text-left"
                            content={
                              isNaN(Number(attr?.matching_value))
                                ? Array.isArray(attr?.matching_value)
                                  ? attr?.matching_value.join(", ")
                                  : typeof attr?.matching_value === "string" &&
                                      attr?.matching_value?.length > 25
                                    ? attr?.matching_value
                                    : ""
                                : ""
                            }
                            contentClassName="max-w-[250px] leading-5 mr-10 capitalize"
                          >
                            <span className="inline-flex space-x-2 items-center">
                              <span>
                                {getArrowIcon(attr?.contribution) ? (
                                  <Image src={`/images/companyinfo/${getArrowIcon(attr?.contribution)}`} />
                                ) : (
                                  <>
                                    <svg
                                      width="14"
                                      height="2"
                                      viewBox="0 0 14 2"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M1.1665 1H12.8332"
                                        stroke="#535862"
                                        stroke-width="1.66667"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                      />
                                    </svg>
                                  </>
                                )}
                              </span>
                            </span>
                          </TooltipWrapper>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
